import { useMemo } from "react";
import { useCheckout } from "../context/CheckoutContext";
import { useClient } from "ClientContext";
import { parse, format } from "date-fns";

type CompanyTerms = {
  companyName: string;
  contactEmail?: string;
  supportEmail?: string;
  bookingEmail?: string;
  checkInTime?: string;
  checkOutTime?: string;
  cancellationPolicy: string[];
  paymentPolicy?: string;
  houseRules: string | undefined;
};

type UseCompanyTermsResult = {
  effectiveCompanyId: string;
  integration_TYPE?: string;
  terms: CompanyTerms;
  hasCompany: boolean;
  showCancellation: boolean;
};

function toAmPm(time: string) {
  // parse from "HH:mm"
  const parsed = parse(time, "HH:mm", new Date());
  return format(parsed, "h:mm a"); // → "4:00 PM"
}

export function useCompanyTerms(companyId?: string): UseCompanyTermsResult {
  const { integration_TYPE, internal_ID } = useClient();
  const { listing, context_quote } = useCheckout();

  const effectiveCompanyId =
    companyId ||
    internal_ID ||
    (listing as any)?.companyId ||
    (listing as any)?.company?.id ||
    (context_quote as any)?.companyId ||
    "";

  const fallbackName: string | undefined =
    (listing as any)?.companyName ||
    (listing as any)?.company?.name ||
    (context_quote as any)?.companyName;

  const terms = useMemo<CompanyTerms>(() => {
    switch (effectiveCompanyId) {
      case "66945d50-54fc-45ea-9f53-6e2395f28c21":
        return {
          companyName: "Bijoux Gateway",
          contactEmail: "hello@bijougetaways.com",
          supportEmail: "hello@bijougetaways.com",
          bookingEmail: "hello@bijougetaways.com",
          checkInTime: listing?.thingsToknow?.checkInTime
            ? toAmPm(listing.thingsToknow.checkInTime)
            : "Contact-Us",
          checkOutTime: listing?.thingsToknow?.checkOutTime
            ? toAmPm(listing.thingsToknow.checkOutTime)
            : "Contact-Us",
          cancellationPolicy: ["refer to house Rules section"],
          paymentPolicy: "Full payment required at time of booking.",
          houseRules: `${listing?.publicDescription?.houseRules || ""} ${
            listing?.publicDescription?.notes || ""
          }`.trim(),
        };

      case "13bac3c5-d8af-409d-b408-006a0146e5f1":
        return {
          companyName: "TheBrassRoom",
          contactEmail: "hello@thebrassroom.co",
          supportEmail: "hello@thebrassroom.co",
          bookingEmail: "hello@thebrassroom.co",
          checkInTime: listing?.thingsToknow?.checkInTime
            ? toAmPm(listing.thingsToknow.checkInTime)
            : "Contact-Us",
          checkOutTime: listing?.thingsToknow?.checkOutTime
            ? toAmPm(listing.thingsToknow.checkOutTime)
            : "Contact-Us",
          cancellationPolicy: ["refer to house Rules section"],

          paymentPolicy: "Full payment required at time of booking.",
          houseRules: "",
        };

      default:
        return {
          companyName: fallbackName || "",
          contactEmail: "",
          supportEmail: "",
          bookingEmail: "",
          checkInTime: "3:00 PM",
          checkOutTime: "11:00 AM",
          cancellationPolicy: [
            "100% refund for cancellations made at least 90 days before check-in.",
            "75% refund for cancellations made at least 60 days before check-in.",
            "50% refund for cancellations made at least 30 days before check-in.",
            "25% refund for cancellations made at least 14 days before check-in.",
            "No refund for cancellations made less than 14 days before check-in.",
          ],
          paymentPolicy: "Full payment required at time of booking.",
          houseRules: "",
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId, fallbackName]);

  const hasCompany = Boolean(
    terms.companyName && terms.companyName.trim().length
  );
  const showCancellation =
    integration_TYPE !== "guesty" && terms.cancellationPolicy.length > 0;

  return {
    effectiveCompanyId,
    integration_TYPE,
    terms,
    hasCompany,
    showCancellation,
  };
}

export default useCompanyTerms;
